import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  FiHome,
  FiBookOpen,
  FiFileText,
  FiAward,
  FiCalendar,
  FiUsers,
  FiSettings,
  FiX,
  FiBarChart,
  FiCheckSquare,
  FiSearch,
  FiDollarSign,
  FiTag,
  FiPackage,
  FiShoppingBag,
  FiCreditCard,
  FiEdit3,
  FiBook,
  FiHeart,
  FiTrash2,
  FiDownload
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import quizApiService from '../../services/quizApi';
import { getCurrentWeekNumber } from '../../utils/datetime-comprehensive';
import logger from '../../utils/logger';

const SidebarContainer = styled(motion.aside).withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})`
  width: 280px;
  background-color: var(--background-primary);
  border-right: 1px solid var(--border-color);
  height: calc(100vh - 64px);
  overflow-y: auto;
  position: sticky;
  top: 64px;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 56px;
    left: ${props => props.isOpen ? '0' : '-280px'};
    z-index: 1000;
    transition: left 0.3s ease;
    box-shadow: var(--shadow-lg);
    height: calc(100vh - 56px);
  }
`;

const SidebarHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--background-secondary);
  text-align: center;
  
  h3 {
    color: var(--text-primary);
    font-family: 'Inter', sans-serif;
    font-size: 1.5rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
    letter-spacing: -0.025em;
  }
  
  p {
    color: var(--text-muted);
    font-size: 0.875rem;
    font-family: 'Inter', sans-serif;
  }
`;

const SearchSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--background-primary);
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: 0;
  font-size: 0.875rem;
  background-color: var(--background-secondary);
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background-color: var(--background-primary);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  color: var(--text-muted);
  z-index: 1;
`;

const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  display: ${props => props.$show ? 'block' : 'none'};
`;

const SearchResultItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid var(--border-color);
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: var(--background-secondary);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SearchResultTitle = styled.div`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

const SearchResultSubtitle = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const NoResults = styled.div`
  padding: 1rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.875rem;
`;


const NavSection = styled.div`
  padding: 1rem 0;
`;

const SectionTitle = styled.h4`
  color: var(--accent-color);
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.15px;
  padding: 0 1.5rem;
  margin-bottom: 0.5rem;
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li`
  margin: 0;
`;

const NavLinkStyled = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  color: var(--text-secondary);
  text-decoration: none;
  transition: var(--transition);
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  
  &:hover {
    background-color: var(--background-secondary);
    color: var(--primary-color);
  }
  
  &.active {
    background-color: rgba(216, 184, 137, 0.1);
    color: var(--primary-color);
    border-right: 3px solid var(--primary-color);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const MobileOverlay = styled(motion.div).withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})`
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  z-index: 999;
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: var(--transition);
  display: none;
  
  &:hover {
    background-color: var(--background-secondary);
    color: var(--text-primary);
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const QuickStats = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color);
  background-color: var(--background-secondary);
`;

const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .label {
    color: var(--text-secondary);
    font-size: 0.875rem;
  }
  
  .value {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 0.875rem;
  }
`;

const navigationItems = [
  {
    section: 'Main',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: FiHome },
      { path: '/catalog', label: 'Course Registration', icon: FiBookOpen, studentOnly: true },
      { path: '/courses', label: 'My Courses', icon: FiBookOpen },
      { path: '/assignments', label: 'My Assignments', icon: FiFileText, studentOnly: true },
      { path: '/attendance', label: 'My Attendance', icon: FiCheckSquare, studentOnly: true },
      { path: '/student/quizzes', label: 'My Quizzes & Exams', icon: FiEdit3, studentOnly: true },
      { path: '/grades', label: 'My Grades', icon: FiAward, studentOnly: true },
      { path: '/student/notes', label: 'My Notes', icon: FiBook, studentOnly: true },
      { path: '/electives', label: 'My Electives', icon: FiBookOpen, studentOnly: true },
      { path: '/assignments', label: 'Assignments', icon: FiFileText, instructorOnly: true },
      { path: '/student/group-poll', label: 'Group Poll', icon: FiBarChart, studentOnly: true },
      { path: '/instructor/quizzes', label: 'Quizzes', icon: FiEdit3, instructorOnly: true },
      { path: '/instructor/gradebook', label: 'Gradebook', icon: FiAward, instructorOnly: true },
      { path: '/calendar', label: 'Calendar', icon: FiCalendar },
      { path: '/payment-history', label: 'Payment History', icon: FiCreditCard, studentOnly: true },
      { path: '/donation', label: 'Make a Donation', icon: FiHeart },
      { path: '/settings', label: 'Settings', icon: FiSettings }
    ]
  },
  {
    section: 'Community',
    items: [
      { path: '/students', label: 'Students', icon: FiUsers }
    ]
  },
  {
    section: 'Administration',
    items: [
      { path: '/admin', label: 'Admin Dashboard', icon: FiHome, adminOnly: true },
      { path: '/admin/users', label: 'User Management', icon: FiUsers, adminOnly: true },
      { path: '/admin/courses', label: 'Course Management', icon: FiBookOpen, instructorOnly: true },
      { path: '/admin/electives', label: 'Elective Management', icon: FiBookOpen, instructorOnly: true },
      { path: '/admin/attendance', label: 'Attendance Management', icon: FiCheckSquare, instructorOnly: true },
      { path: '/admin/deleted-items', label: 'Recently Deleted', icon: FiTrash2, adminOnly: true },
      { path: '/admin/import-export', label: 'Import/Export', icon: FiDownload, adminOnly: true },
      { path: '/instructor/polls', label: 'Poll Manager', icon: FiBarChart, instructorOnly: true },
      { path: '/instructor/live-poll', label: 'Live Poll Board', icon: FiBarChart, instructorOnly: true },
      { path: '/admin/pricing', label: 'Course Pricing', icon: FiDollarSign, adminOnly: true },
      { path: '/admin/promo-codes', label: 'Promo Codes', icon: FiTag, adminOnly: true },
      { path: '/admin/bulk-pricing', label: 'Bulk Pricing', icon: FiPackage, adminOnly: true },
      { path: '/admin/additional-products', label: 'Additional Products', icon: FiShoppingBag, adminOnly: true },
      { path: '/admin/purchase-lookup', label: 'Purchase Lookup', icon: FiCreditCard, adminOnly: true },
      { path: '/admin/reports', label: 'Enrollment Reports', icon: FiBarChart, adminOnly: true }
    ]
  }
];

function Sidebar({ isOpen, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    courses: 0,
    pending: 0,
    completed: 0
  });

  // Search functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Calculate pending tasks (same logic as Dashboard)
  const calculatePendingTasks = useCallback((allActiveAssignments, quizzes) => {
    let pendingCount = 0;

    // Count pending non-weekly assignments
    const nonWeeklyAssignments = allActiveAssignments.filter(a => !a.allow_weekly_submissions && !a.submitted_at);
    pendingCount += nonWeeklyAssignments.length;

    // Count pending weekly assignments (by week)
    const weeklyAssignments = allActiveAssignments.filter(a => a.allow_weekly_submissions);
    for (const assignment of weeklyAssignments) {
      // Calculate total weeks based on start_date
      let totalWeeks = 0;
      if (assignment.start_date) {
        const breakPeriods = Array.isArray(assignment.break_periods) ? assignment.break_periods : [];
        const currentWeek = getCurrentWeekNumber(assignment.start_date, null, breakPeriods);
        const parsedMaxWeeks = Number(assignment.max_weekly_submissions);
        const maxWeeks = Number.isFinite(parsedMaxWeeks) && parsedMaxWeeks > 0
          ? Math.floor(parsedMaxWeeks)
          : 1;
        totalWeeks = Math.min(currentWeek > 0 ? currentWeek : 0, maxWeeks);
      }

      let submittedWeeks = assignment.submitted_week_numbers || [];
      if (typeof submittedWeeks === 'string') {
        try {
          submittedWeeks = JSON.parse(submittedWeeks);
        } catch (e) {
          submittedWeeks = [];
        }
      }
      const pendingWeeks = totalWeeks - (Array.isArray(submittedWeeks) ? submittedWeeks.length : 0);
      pendingCount += Math.max(0, pendingWeeks);
    }

    // Count pending quizzes (quizzes that haven't been started or are in progress)
    const pendingQuizzes = quizzes.filter(q =>
      (q.course_is_active === 1 || q.course_is_active === true) &&
      (q.status === 'not_started' || q.status === 'in_progress')
    ).length;
    pendingCount += pendingQuizzes;

    return pendingCount;
  }, []);

  // Calculate completed tasks (same logic as Dashboard)
  const calculateCompletedTasks = useCallback((allActiveAssignments, quizzes) => {
    let completedCount = 0;

    // Count completed non-weekly assignments
    const nonWeeklyAssignments = allActiveAssignments.filter(a => !a.allow_weekly_submissions && a.submitted_at);
    completedCount += nonWeeklyAssignments.length;

    // Count completed weekly assignments (by week)
    const weeklyAssignments = allActiveAssignments.filter(a => a.allow_weekly_submissions);
    for (const assignment of weeklyAssignments) {
      let submittedWeeks = assignment.submitted_week_numbers || [];
      if (typeof submittedWeeks === 'string') {
        try {
          submittedWeeks = JSON.parse(submittedWeeks);
        } catch (e) {
          submittedWeeks = [];
        }
      }
      completedCount += Array.isArray(submittedWeeks) ? submittedWeeks.length : 0;
    }

    // Count completed quizzes (only quizzes with status 'completed')
    const completedQuizzes = quizzes.filter(q =>
      (q.course_is_active === 1 || q.course_is_active === true) &&
      q.status === 'completed'
    ).length;
    completedCount += completedQuizzes;

    return completedCount;
  }, []);

  useEffect(() => {
    if (user && user.role === 'student') {
      fetchStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Search functionality
  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    logger.debug('Searching for', { query });
    setIsSearching(true);
    try {
      const results = [];
      
      // Use dedicated search API endpoint
      try {
        logger.debug('Using search API...');
        const searchResponse = await apiService.search(query, 'all');
        
        if (searchResponse && searchResponse.success !== false) {
          // Handle search API response - structure: { success: true, results: { courses: [], assignments: [], ... } }
          const searchResults = searchResponse.results || searchResponse.data || searchResponse;
          
          // Process courses
          if (searchResults.courses && Array.isArray(searchResults.courses)) {
            searchResults.courses.forEach(course => {
              results.push({
                id: course.id,
                title: course.name || course.title,
                subtitle: `Course${course.code ? ` - ${course.code}` : ''}`,
                type: 'course',
                path: `/courses/${course.id}`
              });
            });
          }
          
          // Process assignments
          if (searchResults.assignments && Array.isArray(searchResults.assignments)) {
            searchResults.assignments.forEach(assignment => {
              results.push({
                id: assignment.id,
                title: assignment.title,
                subtitle: `Assignment${assignment.course_name ? ` - ${assignment.course_name}` : ''}`,
                type: 'assignment',
                path: user?.role === 'student'
                  ? `/assignments?assignmentId=${encodeURIComponent(assignment.id)}`
                  : `/assignments/${assignment.id}`
              });
            });
          }

          // Process quizzes/exams
          if (searchResults.quizzes && Array.isArray(searchResults.quizzes)) {
            searchResults.quizzes.forEach(quiz => {
              const quizId = quiz.assignment_id || quiz.quiz_id || quiz.id;
              let path = '/student/quizzes';

              if (user?.role === 'student') {
                if (quiz.submitted_at && quiz.latest_attempt_id) {
                  path = `/student/result/${quiz.latest_attempt_id}`;
                } else if (quiz.latest_attempt_id) {
                  path = `/student/attempt/${quiz.latest_attempt_id}`;
                } else if (quiz.assignment_id) {
                  path = `/student/assignments/${quiz.assignment_id}/warning`;
                }
              } else if (quiz.quiz_id || quiz.id) {
                path = `/instructor/quizzes`;
              }

              results.push({
                id: quizId,
                title: quiz.title,
                subtitle: `Quiz/Exam${quiz.course_name ? ` - ${quiz.course_name}` : ''}`,
                type: 'quiz',
                path
              });
            });
          }

          // Process notes
          if (searchResults.notes && Array.isArray(searchResults.notes)) {
            searchResults.notes.forEach(note => {
              results.push({
                id: note.id,
                title: note.title || 'Untitled note',
                subtitle: `Note${note.course_name ? ` - ${note.course_name}` : ''}${note.week_number ? ` - Week ${note.week_number}` : ''}`,
                type: 'note',
                path: `/student/notes/${encodeURIComponent(note.id)}`
              });
            });
          }

          if (user?.role === 'student' && !results.some(result => result.type === 'quiz')) {
            try {
              const quizResponse = await apiService.request('/quizzes/student/assignments?limit=100');
              const quizAssignments = quizResponse?.data || quizResponse?.assignments || [];
              quizAssignments
                .filter(quiz => {
                  const normalizedQuery = query.toLowerCase();
                  return [quiz.title, quiz.description, quiz.course_name, quiz.course_code]
                    .filter(Boolean)
                    .some(value => String(value).toLowerCase().includes(normalizedQuery));
                })
                .slice(0, 10)
                .forEach(quiz => {
                  let path = '/student/quizzes';
                  if (quiz.submitted_at && quiz.latest_attempt_id) {
                    path = `/student/result/${quiz.latest_attempt_id}`;
                  } else if (quiz.latest_attempt_id) {
                    path = `/student/attempt/${quiz.latest_attempt_id}`;
                  } else if (quiz.assignment_id) {
                    path = `/student/assignments/${quiz.assignment_id}/warning`;
                  }

                  results.push({
                    id: quiz.assignment_id || quiz.quiz_id,
                    title: quiz.title,
                    subtitle: `Quiz/Exam${quiz.course_name ? ` - ${quiz.course_name}` : ''}`,
                    type: 'quiz',
                    path
                  });
                });
            } catch (err) {
              logger.warn('Error in supplemental quiz search', { err });
            }
          }
          
          // Process events if available
          if (searchResults.events && Array.isArray(searchResults.events)) {
            searchResults.events.forEach(event => {
              results.push({
                id: event.id,
                title: event.title,
                subtitle: `Event${event.course_name ? ` - ${event.course_name}` : ''}`,
                type: 'event',
                path: `/calendar`
              });
            });
          }
        }
      } catch (error) {
        logger.warn('Search API error, falling back to client-side search', { error, query });
        
        // Fallback: client-side search if API fails
        try {
          const coursesResponse = await apiService.get('/courses');
          if (coursesResponse && coursesResponse.data) {
            const courses = Array.isArray(coursesResponse.data) ? coursesResponse.data : [];
            courses.filter(course =>
              (course.title || course.name) && (
                (course.title || course.name).toLowerCase().includes(query.toLowerCase()) ||
                (course.description && course.description.toLowerCase().includes(query.toLowerCase()))
              )
            ).forEach(course => {
              results.push({
                id: course.id,
                title: course.title || course.name,
                subtitle: 'Course',
                type: 'course',
                path: `/courses/${course.id}`
              });
            });
          }
        } catch (err) {
          logger.warn('Error in fallback course search', { err });
        }
        
        try {
          const assignmentsResponse = await apiService.getAssignments();
          if (assignmentsResponse && assignmentsResponse.success && assignmentsResponse.assignments) {
            assignmentsResponse.assignments.filter(assignment =>
              assignment.title && (
                assignment.title.toLowerCase().includes(query.toLowerCase()) ||
                (assignment.description && assignment.description.toLowerCase().includes(query.toLowerCase()))
              )
            ).forEach(assignment => {
              results.push({
                id: assignment.id,
                title: assignment.title,
                subtitle: `Assignment - ${assignment.course_name || 'Course'}`,
                type: 'assignment',
                path: `/assignments/${assignment.id}`
              });
            });
          }
        } catch (err) {
          logger.warn('Error in fallback assignment search', { err });
        }

        if (user?.role === 'student') {
          try {
            const quizResponse = await apiService.request('/quizzes/student/assignments?limit=100');
            const normalizedQuery = query.toLowerCase();
            const quizAssignments = quizResponse?.data || quizResponse?.assignments || [];
            quizAssignments
              .filter(quiz => [quiz.title, quiz.description, quiz.course_name, quiz.course_code]
                .filter(Boolean)
                .some(value => String(value).toLowerCase().includes(normalizedQuery)))
              .slice(0, 10)
              .forEach(quiz => {
                let path = '/student/quizzes';
                if (quiz.submitted_at && quiz.latest_attempt_id) {
                  path = `/student/result/${quiz.latest_attempt_id}`;
                } else if (quiz.latest_attempt_id) {
                  path = `/student/attempt/${quiz.latest_attempt_id}`;
                } else if (quiz.assignment_id) {
                  path = `/student/assignments/${quiz.assignment_id}/warning`;
                }

                results.push({
                  id: quiz.assignment_id || quiz.quiz_id,
                  title: quiz.title,
                  subtitle: `Quiz/Exam${quiz.course_name ? ` - ${quiz.course_name}` : ''}`,
                  type: 'quiz',
                  path
                });
              });
          } catch (err) {
            logger.warn('Error in fallback quiz search', { err });
          }

          try {
            const notesResponse = await apiService.request(`/notes/mine?search=${encodeURIComponent(query)}&pageSize=10`);
            const notes = notesResponse?.notes || notesResponse?.data?.notes || [];
            notes.forEach(note => {
              results.push({
                id: note.id,
                title: note.title || 'Untitled note',
                subtitle: `Note${note.course_name ? ` - ${note.course_name}` : ''}`,
                type: 'note',
                path: `/student/notes/${encodeURIComponent(note.id)}`
              });
            });
          } catch (err) {
            logger.warn('Error in fallback notes search', { err });
          }
        }
      }

      // Fallback: search navigation items if no results
      if (results.length === 0) {
        logger.debug('No API results, searching navigation items...');
        navigationItems.forEach(section => {
          section.items.forEach(item => {
            if (item.label && item.label.toLowerCase().includes(query.toLowerCase())) {
              results.push({
                id: item.path,
                title: item.label,
                subtitle: section.section,
                type: 'navigation',
                path: item.path
              });
            }
          });
        });
      }

      logger.debug('Final search results', { count: results.length });
      setSearchResults(results.slice(0, 20));
      setShowSearchResults(true);
    } catch (error) {
      logger.error('Search error', { error, query });
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchResultClick = (result) => {
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchResults(false);
    onClose(); // Close sidebar on mobile
    navigate(result.path);
  };

  const handleSearchBlur = () => {
    // Delay hiding results to allow clicking on them
    setTimeout(() => {
      setShowSearchResults(false);
    }, 200);
  };

  const fetchStats = async () => {
    try {
      // Fetch same data as Dashboard for accurate calculations
      const [statsResponse, assignmentsResponse, quizzesResponse] = await Promise.allSettled([
        apiService.getDashboardStats(),
        apiService.getAssignments(),
        quizApiService.getStudentAssignments()
      ]);

      // Extract active courses count from stats
      let activeCourses = 0;
      if (statsResponse.status === 'fulfilled' && statsResponse.value?.success && statsResponse.value?.stats) {
        activeCourses = statsResponse.value.stats.totalCourses || 0;
      }

      // Get assignments from active courses
      let allActiveAssignments = [];
      if (assignmentsResponse.status === 'fulfilled' && assignmentsResponse.value?.success && assignmentsResponse.value?.assignments) {
        allActiveAssignments = assignmentsResponse.value.assignments.filter(
          assignment => assignment.course_is_active === 1 || assignment.course_is_active === true
        );
      }

      // Get quizzes
      let quizzes = [];
      if (quizzesResponse.status === 'fulfilled' && quizzesResponse.value?.success && quizzesResponse.value?.data) {
        quizzes = quizzesResponse.value.data;
      }

      // Calculate pending and completed tasks using same logic as Dashboard
      const pendingTasks = calculatePendingTasks(allActiveAssignments, quizzes);
      const completedTasks = calculateCompletedTasks(allActiveAssignments, quizzes);

      setStats({
        courses: activeCourses,
        pending: pendingTasks,
        completed: completedTasks
      });
    } catch (error) {
      logger.error('Error fetching stats', { error });
      setStats({
        courses: 0,
        pending: 0,
        completed: 0
      });
    }
  };

  return (
    <>
      <MobileOverlay 
        isOpen={isOpen} 
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      />
      
      <SidebarContainer
        isOpen={isOpen}
        initial={{ x: -280 }}
        animate={{ x: 0 }}
        transition={{ duration: 0.3 }}
      >
        <CloseButton onClick={onClose}>
          <FiX size={20} />
        </CloseButton>
        
        <SidebarHeader>
          <h3>Navigation</h3>
          <p>Access your learning tools</p>
        </SidebarHeader>

        <SearchSection>
          <SearchContainer>
            <SearchIcon>
              <FiSearch size={16} />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search the portal..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setShowSearchResults(searchResults.length > 0)}
              onBlur={handleSearchBlur}
            />
            <SearchResults $show={showSearchResults}>
              {isSearching ? (
                <NoResults>Searching...</NoResults>
              ) : searchResults.length > 0 ? (
                searchResults.map((result) => (
                  <SearchResultItem
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleSearchResultClick(result)}
                  >
                    <SearchResultTitle>{result.title}</SearchResultTitle>
                    <SearchResultSubtitle>{result.subtitle}</SearchResultSubtitle>
                  </SearchResultItem>
                ))
              ) : searchTerm ? (
                <NoResults>No results found</NoResults>
              ) : null}
            </SearchResults>
          </SearchContainer>
        </SearchSection>

        {navigationItems
          .map((section, index) => (
            <NavSection key={index}>
              <SectionTitle>{section.section}</SectionTitle>
              <NavList>
                {section.items
                  .filter(item => {
                    // Show item if no role restrictions
                    if (!item.adminOnly && !item.instructorOnly && !item.studentOnly) {
                      return true;
                    }
                    // Show admin-only items only to admins
                    if (item.adminOnly && user?.role === 'admin') {
                      return true;
                    }
                    // Show instructor-only items to instructors and admins
                    if (item.instructorOnly && (user?.role === 'instructor' || user?.role === 'admin')) {
                      return true;
                    }
                    // Show student-only items only to students
                    if (item.studentOnly && user?.role === 'student') {
                      return true;
                    }
                    return false;
                  })
                  .map((item) => {
                    const Icon = item.icon;
                    // Show "Courses" for instructors and admins, "My Courses" for students
                    const displayLabel = (item.path === '/courses' && (user?.role === 'instructor' || user?.role === 'admin')) 
                      ? 'Courses' 
                      : item.label;
                    return (
                      <NavItem key={item.path}>
                        <NavLinkStyled 
                          to={item.path} 
                          onClick={onClose}
                          end={item.path === '/admin'} // Only match exact path for admin dashboard
                        >
                          <Icon />
                          {displayLabel}
                        </NavLinkStyled>
                      </NavItem>
                    );
                  })}
              </NavList>
            </NavSection>
          ))}

        {user?.role === 'student' && (
          <QuickStats>
            <StatItem>
              <span className="label">Active Courses</span>
              <span className="value">{stats.courses}</span>
            </StatItem>
            <StatItem>
              <span className="label">Pending Tasks</span>
              <span className="value">{stats.pending}</span>
            </StatItem>
            <StatItem>
              <span className="label">Completed Tasks</span>
              <span className="value">{stats.completed}</span>
            </StatItem>
          </QuickStats>
        )}
      </SidebarContainer>
    </>
  );
}

export default Sidebar;
